import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AxiosResponse } from 'axios';

import { ConfigOptionsApi } from '@/api';
import { useCFSContext } from '@/hooks';
import { useKaseContext } from '@/hooks/useKaseContext';
import {
  MasterNameInvolvement,
  IKaseInvolvement,
  IKaseMasterName,
  ICFSMasterName,
  ICFSInvolvement,
} from '@/models';
import { IDriverLicense } from '@/models/driver.license';
import { IMasterName, IMasterNamePerson } from '@/models/master.names';
import { INote } from '@/models/note';
import { setLocalStorageItem } from '@/utils';

export interface ExtendedMasterName {
  isTemporary?: boolean;
}

interface MasterNamesContextProviderProps extends PropsWithChildren {
  nextRoutes: string[];
  handleSubmit: (data: IMasterName) => void;
  handleSearchCase?: () => void;
  handleSearchOwners?: () => void;
  showBackButton?: boolean;
  involvementOptions: typeof MasterNameInvolvement;
  addLinkedName?: (
    nameId: string,
    data?: { relationship: ICFSInvolvement },
  ) => Promise<AxiosResponse<ICFSMasterName | IKaseMasterName>>;
  updateLinkedName?: (
    nameId: string,
    data: { relationship: ICFSInvolvement | IKaseInvolvement },
  ) => Promise<AxiosResponse<ICFSMasterName | IKaseMasterName>>;
  onCountsNeedRefresh?: () => void;

  updateMasterName?: (
    nameId: string,
    data: any,
  ) => Promise<AxiosResponse<IMasterName>>;

  getLinkedMasterName?: (
    nameId: string,
  ) => Promise<AxiosResponse<ICFSMasterName | IKaseMasterName>>;
  getLinkedMasterNames?: () => Promise<
    AxiosResponse<(ICFSMasterName | IKaseMasterName)[]>
  >;
  saveNote?: (
    nameId: string,
    description: string,
    noteId?: string,
  ) => Promise<AxiosResponse<INote | undefined, any>>;
  deleteLinkedName?: (nameId: string) => Promise<AxiosResponse>;
  getMasterName?: (nameId: string) => Promise<AxiosResponse<IMasterName>>;
}

interface MasterNamesContextValues
  extends Omit<MasterNamesContextProviderProps, 'children'> {
  linkedMasterNames: (ICFSMasterName | IKaseMasterName)[];
  updateLinkedMasterNames: Dispatch<
    SetStateAction<(ICFSMasterName | IKaseMasterName)[]>
  >;
  currentMastername?: ICFSMasterName | IKaseMasterName;
  currentDriverLicenseId?: string;
  setCurrentMastername: Dispatch<
    SetStateAction<
      ((ICFSMasterName | IKaseMasterName) & ExtendedMasterName) | undefined
    >
  >;
  setCurrentDriverLicenseId: (licenseId: string) => void;
  handleDeleteLinkedName: (nameId: string) => Promise<void>;
  handleAddLinkedName: (
    nameId: string,
    data?: {
      relationship: ICFSInvolvement;
    },
  ) => Promise<void>;
  handleUpdateMasterName: (
    nameId: string,
    data: {
      firstName: string;
      lastName: string;
      driverLicense: string;
    },
  ) => Promise<AxiosResponse<IMasterName, any> | undefined>;
  handleUpdateLinkedName: (
    nameId: string,
    data: {
      relationship: ICFSInvolvement | IKaseInvolvement;
    },
  ) => Promise<void>;
  handleSaveNote: (
    nameId: string,
    description: string,
    noteId?: string,
  ) => Promise<AxiosResponse<INote | undefined, any> | undefined>;
  handleGetLinkedMasterName: (
    nameId: string,
  ) => Promise<ICFSMasterName | IKaseMasterName | undefined>;
  getLinkedMasterNames?: () => Promise<
    AxiosResponse<(ICFSMasterName | IKaseMasterName)[]>
  >;
  temporaryDriverLicense: IDriverLicense | null;
  setTemporaryDriverLicense: (license: IDriverLicense | null) => void;
  source: 'kase' | 'cfs' | 'master-index';
  onCountsNeedRefresh?: () => void;
}

const MasterNamesContext = createContext<MasterNamesContextValues | undefined>(
  undefined,
);

export function useMasterNamesContext() {
  const context = useContext(MasterNamesContext);
  if (!context) {
    throw new Error(
      'useMasterNamesContext must be used within a MasterNamesContextProvider',
    );
  }
  return context;
}

export function MasterNamesContextProvider({
  children,
  addLinkedName,
  updateLinkedName,
  updateMasterName,
  saveNote,
  deleteLinkedName,
  getLinkedMasterName,
  getLinkedMasterNames,
  involvementOptions,
  getMasterName,
  onCountsNeedRefresh,
  ...rest
}: MasterNamesContextProviderProps) {
  const [currentMastername, setCurrentMastername] = useState<
    ((ICFSMasterName | IKaseMasterName) & ExtendedMasterName) | undefined
  >();
  const [linkedMasterNames, setLinkedMasterNames] = useState<
    (ICFSMasterName | IKaseMasterName)[]
  >([]);
  const [currentDriverLicenseId, setCurrentDriverLicenseId] =
    useState<string>();
  const [temporaryDriverLicense, setTemporaryDriverLicense] =
    useState<IDriverLicense | null>(null);
  const { cfs, updateCFS, fetchCFS } = useCFSContext();
  const { kase, updateKase } = useKaseContext();
  const source = kase?._id ? 'kase' : cfs?._id ? 'cfs' : 'master-index';

  useEffect(() => {
    if (!kase || involvementOptions !== MasterNameInvolvement) return;
    const formNames = ['OFFENDER', 'VICTIM', 'WITNESS'] as const;
    formNames.forEach(async (formName) => {
      try {
        const { data } = await ConfigOptionsApi.getFormConfig(formName);
        setLocalStorageItem({ key: formName, value: data });
      } catch {
        // Ignore prefetch errors; loader will fetch on demand.
      }
    });
  }, [kase, involvementOptions]);

  const handleAddLinkedName = async (
    id: string,
    data?: { relationship: ICFSInvolvement },
  ) => {
    if (addLinkedName) {
      const res = await addLinkedName(id, data);
      setLinkedMasterNames((v) => [...v, res.data]);
      if (
        data?.relationship.involvement?.includes(MasterNameInvolvement.CALLER)
      ) {
        if (cfs && !cfs.primaryReporter) {
          updateCFS({
            ...cfs,
            primaryReporter: res.data.masterName as IMasterNamePerson,
          });
        }
        if (kase) {
          updateKase({ ...kase });
        }
      }
    }
  };

  const handleUpdateLinkedName = async (
    id: string,
    data: { relationship: ICFSInvolvement | IKaseInvolvement },
  ) => {
    if (updateLinkedName) {
      const res = await updateLinkedName(id, data);
      if (res) {
        const updateLinkNames = linkedMasterNames?.map((item) => {
          if (item.masterName?._id === id) {
            return { ...item, relationship: data.relationship };
          }
          return item;
        });
        if (!updateLinkNames) {
          return;
        }
        setLinkedMasterNames(updateLinkNames as ICFSMasterName[]);

        const isAddingCaller = data.relationship.involvement?.includes(
          MasterNameInvolvement.CALLER,
        );
        const isRemovingCallerFromPrimaryReporter =
          cfs?.primaryReporter?._id === id && !isAddingCaller;

        if (cfs && isAddingCaller) {
          if (!cfs.primaryReporter) {
            updateCFS({
              ...cfs,
              primaryReporter: res.data.masterName as IMasterNamePerson,
            });
          }
        } else if (isRemovingCallerFromPrimaryReporter) {
          const nextCaller = updateLinkNames.find(
            (name) =>
              name.masterName?._id !== id &&
              name.relationship?.involvement?.includes(
                MasterNameInvolvement.CALLER,
              ),
          );
          updateCFS({
            ...cfs,
            primaryReporter: nextCaller?.masterName as
              | IMasterNamePerson
              | undefined,
          });
        }

        // Refresh parent counts after involvement/arrest status update
        if (cfs?._id) {
          void fetchCFS();
        } else {
          onCountsNeedRefresh?.();
        }
      }
    }
  };

  const handleUpdateMasterName = async (id: string, data: any) => {
    if (updateMasterName) {
      const res = await updateMasterName(id, data);
      if (res && getMasterName) {
        const final = await getMasterName(id);
        if (final) {
          setCurrentMastername((prev) => {
            if (!prev) return undefined;
            return {
              ...prev,
              masterName: final.data as IMasterNamePerson,
            };
          });
        }

        return final;
      }
    }
  };
  const handleSaveNote = async (
    nameId: string,
    description: string,
    noteId?: string,
  ) => (saveNote ? saveNote(nameId, description, noteId) : undefined);

  const handleDeleteLinkedName = async (nameId: string) => {
    if (deleteLinkedName) {
      await deleteLinkedName(nameId);
      const updatedLinkedNames = linkedMasterNames.filter(
        ({ masterName }) => masterName?._id !== nameId,
      );
      setLinkedMasterNames(updatedLinkedNames);
      if (cfs?.primaryReporter?._id === nameId) {
        const nextCaller = updatedLinkedNames.find((name) =>
          name.relationship?.involvement?.includes(
            MasterNameInvolvement.CALLER,
          ),
        );
        updateCFS({
          ...cfs,
          primaryReporter: nextCaller?.masterName as
            | IMasterNamePerson
            | undefined,
        });
      }
    }
  };

  const handleGetLinkedMasterName = async (nameId: string) => {
    if (getLinkedMasterName) {
      const res = await getLinkedMasterName(nameId);
      return res ? res.data : undefined;
    }
  };

  return (
    <MasterNamesContext.Provider
      value={{
        linkedMasterNames,
        updateLinkedMasterNames: setLinkedMasterNames,
        currentMastername,
        setCurrentMastername,
        currentDriverLicenseId,
        setCurrentDriverLicenseId,
        handleAddLinkedName,
        handleSaveNote,
        handleUpdateLinkedName,
        handleUpdateMasterName,
        handleDeleteLinkedName,
        handleGetLinkedMasterName,
        getLinkedMasterNames,
        involvementOptions,
        temporaryDriverLicense,
        setTemporaryDriverLicense,
        source,
        onCountsNeedRefresh,
        ...rest,
      }}
    >
      {children}
    </MasterNamesContext.Provider>
  );
}
